import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  PLATFORM_ID,
  inject,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { QueryClient, provideAngularQuery } from '@tanstack/angular-query-experimental';

import en from '@angular/common/locales/en';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { AuthService } from './core/services/auth.service';
import { SentryConfigService } from './core/services/sentry-config.service';
import { SentryService } from './core/services/sentry.service';
import { metaReducers, reducers } from './core/store';

registerLocaleData(en);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideStore(reducers, { metaReducers }),
    provideClientHydration(withEventReplay()),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    SentryService,
    SentryConfigService,
    AuthService,
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const platformId = inject(PLATFORM_ID);
        const authService = inject(AuthService);
        const sentryService = inject(SentryService);
        const sentryConfigService = inject(SentryConfigService);

        return () => {
          // Initialize auth on client side
          if (isPlatformBrowser(platformId)) {
            authService.refreshUser();

            // Skip Sentry if DSN is not configured or in local dev with suppressLocalSentry
            if (
              !environment.sentryDsn ||
              (environment.suppressLocalSentry && !environment.production)
            ) {
              return;
            }

            const config = sentryConfigService.getConfig(
              environment.production ? 'production' : 'staging',
            );

            sentryService.initialize({
              dsn: environment.sentryDsn,
              environment: environment.environmentName,
              release: config.release,
              tracesSampleRate: config.tracesSampleRate,
              replaysSessionSampleRate: config.replaysSessionSampleRate,
              replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
            });
          }
        };
      },
      multi: true,
    },
    provideNzI18n(en_US),
    provideAnimationsAsync(),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: false, // Changed to false - ensure actions are always recorded
      trace: true, // Enable to see stack traces in DevTools
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true, // If set to true, the connection is established within the Angular zone
    }),
    provideAngularQuery(new QueryClient()),
    provideAngularSvgIcon(),
  ],
};
